import React, { useState, useEffect } from 'react';
import { Gift, X, ArrowRight, Zap, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FREE_SUBSCRIPTION_THRESHOLD, BONUS_PER_REFERRAL_ABOVE_THRESHOLD } from '@/components/henvisning/HenvisningConstants';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };

function HenvisningPopup({ onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#2F424D] to-[#1F2E33] px-6 py-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
            aria-label="Luk"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-[#D4A62A] rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(234,179,8,0.4)]">
              <Gift className="w-6 h-6 text-[#2F424D]" />
            </div>
            <div>
              <span className="bg-[#D4A62A] text-[#2F424D] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">NYHED</span>
              <h2 className="text-white text-xl font-bold mt-1" style={serif}>Henvis venner – få gratis el</h2>
            </div>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            Som Enkel-kunde kan du nu tjene på at anbefale os. Henvis venner og familie og spar på din elregning.
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Rewards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#f5f3ee] rounded-xl p-4 border border-[#e8e4d9]">
              <p className="text-xs text-gray-500 mb-1">Med {FREE_SUBSCRIPTION_THRESHOLD} aktive henvisninger</p>
              <p className="text-2xl font-bold text-[#2F424D]" style={serif}>0 kr./md.</p>
              <p className="text-xs text-gray-600 mt-1">Gratis el-abonnement</p>
            </div>
            <div className="bg-[#f5f3ee] rounded-xl p-4 border border-[#e8e4d9]">
              <p className="text-xs text-gray-500 mb-1">Per ekstra henvisning</p>
              <p className="text-2xl font-bold text-[#2F424D]" style={serif}>+{BONUS_PER_REFERRAL_ABOVE_THRESHOLD} kr./md.</p>
              <p className="text-xs text-gray-600 mt-1">Fratrukket din regning</p>
            </div>
          </div>

          {/* How it works — quick version */}
          <div className="space-y-2">
            {[
              { icon: Check, text: 'Del dit personlige link med venner og familie' },
              { icon: Check, text: 'De bestiller elaftale + Saving Mode serviceaftale' },
              { icon: Zap, text: 'Du får fratrukket beløbet automatisk på din elregning' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-gray-700">
                <div className="w-6 h-6 bg-[#2F424D] rounded-full flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-3.5 h-3.5 text-[#D4A62A]" />
                </div>
                {item.text}
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-400 leading-relaxed">
            Kun for aktive Enkel-kunder med elaftale. Én ven tæller når de har aktiv elaftale + Saving Mode serviceaftale og har passeret 14-dages fortrydelsesret.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
          <a
            href="/henvisning"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button className="w-full bg-[#D4A62A] hover:bg-[#c29625] text-gray-900 font-semibold gap-2">
              Tilmeld dig nu <ArrowRight className="w-4 h-4" />
            </Button>
          </a>
          <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-none">
            Luk
          </Button>
        </div>
      </div>
    </div>
  );
}

// variant: 'light' (default) | 'dark'
export default function HenvisningBanner({ variant = 'light' }) {
  const [popupOpen, setPopupOpen] = useState(false);
  const isDark = variant === 'dark';

  return (
    <>
      <button
        onClick={() => setPopupOpen(true)}
        className={`w-full text-left transition-all duration-200 group ${
          isDark
            ? 'hover:bg-white/5 rounded-xl'
            : 'bg-gradient-to-r from-[#2F424D] to-[#1F2E33] hover:from-[#384B58] hover:to-[#273540]'
        }`}
      >
        <div className={`py-3 flex items-center justify-between gap-4 ${isDark ? '' : 'container mx-auto px-6'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-[#D4A62A] rounded-lg flex items-center justify-center flex-shrink-0 shadow-[0_0_12px_rgba(234,179,8,0.4)]">
              <Gift className="w-4 h-4 text-[#2F424D]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 bg-[#D4A62A] text-[#2F424D]">
                  NYHED
                </span>
                <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-white'}`}>
                  Venneprogram – gratis abonnement &amp; gratis el ved henvisninger
                </p>
              </div>
              <p className={`text-xs mt-0.5 hidden sm:block ${isDark ? 'text-gray-400' : 'text-gray-300'}`}>
                Henvis {FREE_SUBSCRIPTION_THRESHOLD} venner → 0 kr./md. abonnement. Ekstra {BONUS_PER_REFERRAL_ABOVE_THRESHOLD} kr./md. per yderligere. Klik for at læse mere.
              </p>
            </div>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1 text-sm font-medium text-[#D4A62A] group-hover:gap-2 transition-all">
            <span className="hidden sm:inline">Læs mere</span>
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </button>

      {popupOpen && <HenvisningPopup onClose={() => setPopupOpen(false)} />}
    </>
  );
}